import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import {
  Users, Stethoscope, Shield,
  RefreshCw, UserCheck, Key, FileCheck
} from 'lucide-react';

const roleIcons = {
  USER: Users,
  DOCTOR: Stethoscope,
  ADMIN: Shield
};

const roleColors = {
  USER: 'bg-blue-50 text-blue-600',
  DOCTOR: 'bg-teal-50 text-teal-600',
  ADMIN: 'bg-purple-50 text-purple-600'
};

const AccessControl = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings/access/roles');
      if (res.data?.success) setData(res.data.data);
    } catch (err) {
      console.error('Failed to load access control data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-40 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const consents = data?.consents || {};

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Access Control</h2>
          <p className="text-slate-500 text-sm mt-1">Role permissions and access policies</p>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Role Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {(data?.roles || []).map((role) => {
          const Icon = roleIcons[role.role] || Users;
          return (
            <div key={role.role} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${roleColors[role.role] || 'bg-slate-50 text-slate-600'}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-extrabold text-slate-900">{role.count}</p>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">{role.label}s</p>
              {role.verificationBreakdown && (
                <div className="flex gap-2 mt-2">
                  <span className="text-xs font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                    {role.verificationBreakdown.approved} approved
                  </span>
                  {role.verificationBreakdown.pending > 0 && (
                    <span className="text-xs font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                      {role.verificationBreakdown.pending} pending
                    </span>
                  )}
                  {role.verificationBreakdown.rejected > 0 && (
                    <span className="text-xs font-bold px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                      {role.verificationBreakdown.rejected} rejected
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Consent Overview */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-primary" /> Data Consent Overview
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Active', value: consents.active || 0, color: 'text-emerald-600' },
            { label: 'Revoked', value: consents.revoked || 0, color: 'text-red-600' },
            { label: 'Expired', value: consents.expired || 0, color: 'text-amber-600' },
            { label: 'Suspended', value: consents.suspended || 0, color: 'text-slate-500' },
          ].map((item, i) => (
            <div key={i} className="p-3 bg-slate-50 rounded-xl text-center">
              <p className={`text-xl font-extrabold ${item.color}`}>{item.value}</p>
              <p className="text-xs font-bold text-slate-500 uppercase">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Security Policies — with real metrics */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" /> Security Policies
          </h3>
        </div>
        {(data?.policies || []).length === 0 ? (
          <p className="p-5 text-sm text-slate-500 text-center">No policy data available.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {(data?.policies || []).map((policy, i) => (
              <div key={i} className="px-5 py-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-900">{policy.name}</p>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${policy.enforced ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {policy.enforced ? 'Enforced' : 'Not Enforced'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{policy.description}</p>
                {policy.metric && (
                  <p className="text-xs font-medium text-primary mt-1">{policy.metric}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" /> Active Sessions
            </h3>
            <p className="text-xs text-slate-500 mt-1">Unique users active in the last 24 hours (from audit logs)</p>
          </div>
          <p className="text-3xl font-extrabold text-primary">{data?.activeSessions || 0}</p>
        </div>
      </div>
    </div>
  );
};

export default AccessControl;
